import React, { useEffect, useCallback } from 'react';
import { connect } from '../../redux/store';
import { actions } from '../../redux/modules/settingsApprovals';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from '../shared/PageTitle';
import {
  LoadingMessage,
  EmptyMessage,
  ErrorMessage,
  PaginationBar,
} from '@kineticdata/bundle-common';
import { List, Map } from 'immutable';

const AgreementTeamsComponent = ({
  init,
  error,
  agreements,
  isCustomerApprovalAdmin,
  teamsMap,
  // Actions
  fetchTeams,
  // Pagination state
  pageSize,
  setPageSize,
  index,
  setIndex,
  // Other state
  profile,
  appLocation,
  isMobile,
  navigate,
}) => {
  useEffect(
    () => {
      fetchTeams();
    },
    [fetchTeams],
  );

  // Filter list of agreements to those the current user can manage
  const managableAgreements = isCustomerApprovalAdmin
    ? agreements
    : agreements.filter(a =>
        profile.memberships.find(m => m.team.name === a.Agreement),
      );

  const count = managableAgreements ? managableAgreements.size : 0;
  const changePageSize = useCallback(
    newPageSize => () => setPageSize(newPageSize),
    [setPageSize],
  );
  const previousPage = useCallback(
    () => setIndex(Math.max(index - pageSize, 0)),
    [index, pageSize, setIndex],
  );
  const nextPage = useCallback(
    () => setIndex(index + pageSize < count ? index + pageSize : index),
    [index, pageSize, count, setIndex],
  );
  const goToPage = useCallback(newPage => () => setIndex(newPage * pageSize), [
    pageSize,
    setIndex,
  ]);

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          parts={['Approval Management']}
          breadcrumbs={[]}
          title="Approval Management"
        />

        {error ? (
          <ErrorMessage message={error.message} />
        ) : !init ? (
          <LoadingMessage />
        ) : (
          <>
            <table className="table table--settings">
              <thead>
                <tr>
                  <th>
                    <I18n>Agreement</I18n>
                  </th>
                  <th>
                    <I18n>Approval Groups</I18n>
                  </th>
                </tr>
              </thead>
              <tbody>
                {managableAgreements.slice(index, index + pageSize).map(a => (
                  <tr key={a.Agreement}>
                    <td>
                      <Link to={a.Agreement}>{a.Agreement}</Link>
                    </td>
                    <td>
                      {
                        (teamsMap.getIn([a.Agreement, 'children']) || List())
                          .size
                      }
                    </td>
                  </tr>
                ))}
                {managableAgreements.size === 0 && (
                  <tr>
                    <td colSpan="2">
                      <EmptyMessage title="No agreements found." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {managableAgreements.size > 0 && (
              <PaginationBar
                className="mb-3"
                previousPage={index > 0 ? previousPage : undefined}
                nextPage={index + pageSize < count ? nextPage : undefined}
                startIndex={index + 1}
                endIndex={Math.max(
                  Math.min(index + pageSize, count),
                  index + 1,
                )}
                count={count}
                countDone={true}
                pageSize={pageSize}
                changePageSize={changePageSize}
                currentPage={Math.floor(index / pageSize)}
                pageCount={Math.ceil(count / pageSize)}
                goToPage={goToPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    agreements: state.settingsApp.agreements,
    isCustomerApprovalAdmin: state.settingsApp.isCustomerApprovalAdmin,
    init:
      !!state.settingsApprovals.teamsMap ||
      !!state.settingsApprovals.teamsError,
    error: state.settingsApprovals.teamsError,
    teamsMap: state.settingsApprovals.teamsMap || Map(),
    // Other state
    profile: state.app.profile,
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchTeams: actions.fetchTeamsRequest,
};

export const AgreementTeams = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AgreementTeamsComponent);
